import { Event } from "../../Models/Event";
import { displayDateOnly } from "../../Utils";
import { format } from "date-fns";
import {
  HiChevronDown,
  HiChevronRight,
  HiDocumentDuplicate,
  HiEye,
  HiOutlineDocumentReport,
  HiPencilAlt,
  HiXCircle,
} from "react-icons/hi";
import { Button, Select } from "flowbite-react";
import { MdOutlinePersonAddAlt } from "react-icons/md";
import { useMemo, useState } from "react";

type EventGroup = {
  parent: Event;
  subEvents: Event[];
};

function getEventTypeLabel(event: Event) {
  if (event.eventType === "Conference" || event.eventType === "SpringConference" || event.eventType === "FallConference") {
    if (event.eventType === "SpringConference") {
      return event.year ? `Spring ${event.year} Conference` : "Spring Conference";
    }
    if (event.eventType === "FallConference") {
      return event.year ? `Fall ${event.year} Conference` : "Fall Conference";
    }
    return event.season && event.year
      ? `${event.season} ${event.year} Conference`
      : "Conference";
  }
  if (event.eventType === "ContactSession") {
    return "Contact Session";
  }
  if (event.eventType === "SubEvent") {
    if (event.parentEventName) {
      const parentLabel = event.parentEventSeason && event.parentEventYear
        ? `${event.parentEventSeason} ${event.parentEventYear} Conference`
        : event.parentEventName;
      return `${parentLabel} Sub-Event`;
    }
    return "Sub-Event";
  }
  return event.isGlobal ? "Global Event" : "Regular Event";
}

function getEventTypeBadgeClass(event: Event) {
  if (event.eventType === "Conference" || event.eventType === "SpringConference" || event.eventType === "FallConference") {
    return "bg-blue-100 text-blue-800";
  }
  if (event.eventType === "ContactSession") {
    return "bg-amber-100 text-amber-900";
  }
  if (event.eventType === "SubEvent") {
    return "bg-gray-100 text-gray-800";
  }
  return "bg-green-100 text-green-800";
}

/** Format time string (HH:mm or HH:mm:ss) to "h:mm a". */
function formatTimeDisplay(t: string | null | undefined): string {
  if (!t?.trim()) return "";
  const parts = t.trim().split(":").map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const d = new Date(2000, 0, 1, h, m);
  return format(d, "h:mm a");
}

/** Date and optional time range for event list. */
function formatEventDateAndTime(event: Event): string {
  const datePart = event.eventDate ? displayDateOnly(new Date(event.eventDate)) : "";
  const start = formatTimeDisplay(event.eventTime);
  const end = formatTimeDisplay(event.eventEndTime ?? null);
  const timePart = start && end ? `${start} – ${end}` : start || end;
  if (!datePart) return "";
  if (!timePart) return datePart;
  return `${datePart} ${timePart}`;
}

function EventRow({
  event,
  handleEditEvent,
  handleAddUser,
  deleteEvent,
  handleShowParticipants,
  handleDownloadReport,
  getEventTypeLabel,
  getEventTypeBadgeClass,
}: {
  event: Event;
  handleEditEvent: (ev: Event) => void;
  handleAddUser: (ev: Event) => void;
  deleteEvent: (ev: Event) => void;
  handleShowParticipants: (ev: Event) => void;
  handleDownloadReport: (ev: Event) => void;
  getEventTypeLabel: (ev: Event) => string;
  getEventTypeBadgeClass: (ev: Event) => string;
}) {
  const participantCount = event.eventParticipants?.length ?? 0;
  const isCopiedNotEdited = event.copiedNotEdited === true;
  return (
    <tr
      className={`border-t border-gray-200 hover:bg-gray-50 ${
        isCopiedNotEdited ? "bg-amber-50" : ""
      }`}
      title={isCopiedNotEdited ? "Copied template - edit to update dates/times" : undefined}
    >
      <td className="px-4 py-2">
        <div className="flex justify-center gap-1">
          <Button className="buttonMain p-2" onClick={() => handleEditEvent(event)} size={20}>
            <HiPencilAlt />
          </Button>
          <Button onClick={() => handleAddUser(event)} className="buttonMainBlue p-2" size={20}>
            <MdOutlinePersonAddAlt />
          </Button>
          <Button onClick={() => handleDownloadReport(event)} className="buttonMain p-2" size={20}>
            <HiOutlineDocumentReport />
          </Button>
        </div>
      </td>
      <td className="px-4 py-2 text-sm">{event.id}</td>
      <td className="px-4 py-2 text-sm">{event.name ?? ""}</td>
      <td className="px-4 py-2">
        <span className={`px-2 py-1 rounded text-xs font-semibold ${getEventTypeBadgeClass(event)}`}>
          {getEventTypeLabel(event)}
        </span>
      </td>
      <td className="px-4 py-2 text-sm">
        {formatEventDateAndTime(event)}
      </td>
      <td className="px-4 py-2">
        <i
          className="flex gap-4 items-center cursor-pointer text-blue-600 hover:underline"
          onClick={() => handleShowParticipants(event)}
        >
          <HiEye />
          {participantCount}
        </i>
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col gap-1 justify-left">
          <a
            href={`/events/${event.id}/attending`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline text-sm"
            title="Public page: registered company names only (shareable / exportable)"
          >
            Registered Companies
          </a>
          <a
            href={`/events/${event.id}/participants`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline text-sm"
            title="Internal attendee list (names by member level)"
          >
            Internal Attendees
          </a>
          {event.eventType === "ContactSession" && (
            <a
              href={`/events/${event.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline text-sm"
            >
              Contact Session Map
            </a>
          )}
        </div>
      </td>
      <td className="px-4 py-2">
        <Button className="buttonCancel p-2" onClick={() => deleteEvent(event)} size={20}>
          <HiXCircle />
        </Button>
      </td>
    </tr>
  );
}

function EventMobileCard({
  event,
  handleEditEvent,
  handleAddUser,
  deleteEvent,
  handleShowParticipants,
  handleDownloadReport,
  getEventTypeLabel,
  getEventTypeBadgeClass,
}: {
  event: Event;
  handleEditEvent: (ev: Event) => void;
  handleAddUser: (ev: Event) => void;
  deleteEvent: (ev: Event) => void;
  handleShowParticipants: (ev: Event) => void;
  handleDownloadReport: (ev: Event) => void;
  getEventTypeLabel: (ev: Event) => string;
  getEventTypeBadgeClass: (ev: Event) => string;
}) {
  const participantCount = event.eventParticipants?.length ?? 0;
  const isCopiedNotEdited = event.copiedNotEdited === true;
  return (
    <div className={`p-3 ${isCopiedNotEdited ? "bg-amber-50" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-gray-900">{event.name ?? "Unnamed"}</p>
          <p className="mt-1 text-sm text-gray-600">{formatEventDateAndTime(event) || "No date"}</p>
        </div>
        <span className={`shrink-0 px-2 py-1 rounded text-xs font-semibold ${getEventTypeBadgeClass(event)}`}>
          {getEventTypeLabel(event)}
        </span>
      </div>
      <button
        type="button"
        className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600"
        onClick={() => handleShowParticipants(event)}
      >
        <HiEye />
        {participantCount} participant{participantCount === 1 ? "" : "s"}
      </button>
      <div className="mt-2 flex flex-col gap-1">
        <a href={`/events/${event.id}/attending`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline">
          Registered Companies
        </a>
        <a href={`/events/${event.id}/participants`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline">
          Internal Attendees
        </a>
        {event.eventType === "ContactSession" && (
          <a href={`/events/${event.id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline">
            Contact Session Map
          </a>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button className="buttonMain" size="sm" onClick={() => handleEditEvent(event)}>
          <HiPencilAlt className="mr-1" /> Edit
        </Button>
        <Button className="buttonMainBlue" size="sm" onClick={() => handleAddUser(event)}>
          <MdOutlinePersonAddAlt className="mr-1" /> Scan
        </Button>
        <Button className="buttonMain" size="sm" onClick={() => handleDownloadReport(event)}>
          <HiOutlineDocumentReport className="mr-1" /> Report
        </Button>
        <Button className="buttonCancel" size="sm" onClick={() => deleteEvent(event)}>
          <HiXCircle className="mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}

function isConferenceType(event: Event) {
  return (
    event.eventType === "Conference" ||
    event.eventType === "SpringConference" ||
    event.eventType === "FallConference"
  );
}

export default function EventsListTable({
  data,
  handleEditEvent,
  handleAddUser,
  deleteEvent,
  handleShowParticipants,
  handleDownloadReport,
  handleCopyConference,
  isSuperUser,
}: {
  data: Event[];
  handleEditEvent: (ev: Event) => void;
  handleAddUser: (ev: Event) => void;
  deleteEvent: (ev: Event) => void;
  handleShowParticipants: (ev: Event) => void;
  handleDownloadReport: (ev: Event) => void;
  handleCopyConference?: (ev: Event) => void;
  isSuperUser?: boolean;
}) {
  const [eventTypeFilter, setEventTypeFilter] = useState<"all" | "conference" | "regular">("all");
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  // Filter events based on selected type
  const filteredData = useMemo(() => {
    if (eventTypeFilter === "all") return data;
    if (eventTypeFilter === "conference") {
      return data.filter(
        (e) =>
          e.eventType === "Conference" ||
          e.eventType === "SpringConference" ||
          e.eventType === "FallConference"
      );
    }
    return data.filter(
      (e) =>
        e.eventType !== "Conference" &&
        e.eventType !== "SpringConference" &&
        e.eventType !== "FallConference"
    );
  }, [data, eventTypeFilter]);

  // Group events by parent: parent events (no parentEventId) + their sub-events
  const groupedEvents = useMemo(() => {
    const groups: EventGroup[] = [];
    const seenParentIds = new Set<number>();

    // Top-level events (no parent) become their own groups
    const topLevel = filteredData.filter((e) => !e.parentEventId);
    topLevel.forEach((parent) => {
      const subEvents = filteredData
        .filter((e) => e.parentEventId === parent.id)
        .sort(
          (a, b) =>
            new Date(b.eventDate ?? 0).getTime() - new Date(a.eventDate ?? 0).getTime()
        );
      groups.push({ parent, subEvents });
      seenParentIds.add(parent.id);
    });

    // Sub-events whose parent isn't in filteredData: get parent from full data
    filteredData
      .filter((e) => e.parentEventId && !seenParentIds.has(e.parentEventId))
      .forEach((sub) => {
        const parentId = sub.parentEventId!;
        const parent = data.find((e) => e.id === parentId);
        if (parent) {
          const subEvents = filteredData
            .filter((e) => e.parentEventId === parentId)
            .sort(
              (a, b) =>
                new Date(b.eventDate ?? 0).getTime() - new Date(a.eventDate ?? 0).getTime()
            );
          groups.push({ parent, subEvents });
          seenParentIds.add(parentId);
        }
      });

    // Sort groups by parent event date (desc)
    groups.sort((a, b) => {
      const dateA = new Date(a.parent.eventDate ?? 0).getTime();
      const dateB = new Date(b.parent.eventDate ?? 0).getTime();
      return dateB - dateA;
    });

    return groups;
  }, [filteredData, data]);

  const toggleGroup = (parentId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(groupedEvents.map((g) => g.parent.id)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const hasSubEvents = groupedEvents.some((g) => g.subEvents.length > 0);

  return (
    <>
      <div className="w-full mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-gray-700">Filter by Type:</label>
          <Select
            value={eventTypeFilter}
            onChange={(e) =>
              setEventTypeFilter(e.target.value as "all" | "conference" | "regular")
            }
            className="w-48"
          >
            <option value="all">All Events</option>
            <option value="conference">Conferences Only</option>
            <option value="regular">Regular Events Only</option>
          </Select>
          {hasSubEvents && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={expandAll}
                className="text-sm text-blue-600 hover:underline"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={collapseAll}
                className="text-sm text-blue-600 hover:underline"
              >
                Collapse all
              </button>
            </div>
          )}
          <span className="text-sm text-gray-600">
            Showing {filteredData.length} of {data.length} events
          </span>
        </div>
      </div>

      <div className="w-full border rounded-lg overflow-hidden">
        {groupedEvents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No events to display</div>
        ) : (
        groupedEvents.map((group) => {
          const isExpanded = expandedGroups.has(group.parent.id);
          const hasSubs = group.subEvents.length > 0;

          return (
            <div key={group.parent.id} className="border-b border-gray-200 last:border-b-0">
              <div className="w-full flex flex-col gap-3 px-3 py-3 bg-gray-50 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.parent.id)}
                  className="flex flex-wrap items-center gap-2 min-w-0 flex-1 text-left"
                >
                  {hasSubs ? (
                    isExpanded ? (
                      <HiChevronDown className="h-5 w-5 text-gray-500 shrink-0" />
                    ) : (
                      <HiChevronRight className="h-5 w-5 text-gray-500 shrink-0" />
                    )
                  ) : (
                    <span className="w-5 shrink-0" />
                  )}
                  <span className="font-medium text-gray-900">{group.parent.name ?? "Unnamed"}</span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-semibold shrink-0 ${getEventTypeBadgeClass(
                      group.parent
                    )}`}
                  >
                    {getEventTypeLabel(group.parent)}
                  </span>
                  {group.parent.eventDate && (
                    <span className="text-sm text-gray-500">
                      {displayDateOnly(new Date(group.parent.eventDate))}
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    {group.parent.eventParticipants?.length ?? 0} participants
                  </span>
                  {hasSubs && (
                    <span className="text-sm text-gray-500">
                      {group.subEvents.length} sub-event{group.subEvents.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </button>
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`/events/${group.parent.id}/attending`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:underline"
                    title="Public registered companies page"
                  >
                    Registered Companies
                  </a>
                  {isSuperUser && handleCopyConference && isConferenceType(group.parent) && (
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleCopyConference(group.parent)}
                      title="Copy as template for new conference"
                    >
                      <HiDocumentDuplicate className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                  )}
                </div>
              </div>

              {(!hasSubs || isExpanded) && (
                <div className="bg-white">
                  <div className="md:hidden divide-y divide-gray-200">
                    <EventMobileCard
                      event={group.parent}
                      handleEditEvent={handleEditEvent}
                      handleAddUser={handleAddUser}
                      deleteEvent={deleteEvent}
                      handleShowParticipants={handleShowParticipants}
                      handleDownloadReport={handleDownloadReport}
                      getEventTypeLabel={getEventTypeLabel}
                      getEventTypeBadgeClass={getEventTypeBadgeClass}
                    />
                    {group.subEvents.map((sub) => (
                      <EventMobileCard
                        key={sub.id}
                        event={sub}
                        handleEditEvent={handleEditEvent}
                        handleAddUser={handleAddUser}
                        deleteEvent={deleteEvent}
                        handleShowParticipants={handleShowParticipants}
                        handleDownloadReport={handleDownloadReport}
                        getEventTypeLabel={getEventTypeLabel}
                        getEventTypeBadgeClass={getEventTypeBadgeClass}
                      />
                    ))}
                  </div>
                  <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Actions</th>
                        <th className="text-left px-4 py-2 font-medium">Id</th>
                        <th className="text-left px-4 py-2 font-medium">Name</th>
                        <th className="text-left px-4 py-2 font-medium">Type</th>
                        <th className="text-left px-4 py-2 font-medium">Date</th>
                        <th className="text-left px-4 py-2 font-medium">Participants</th>
                        <th className="text-left px-4 py-2 font-medium">Company / Attendee Links</th>
                        <th className="text-left px-4 py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <EventRow
                        event={group.parent}
                        handleEditEvent={handleEditEvent}
                        handleAddUser={handleAddUser}
                        deleteEvent={deleteEvent}
                        handleShowParticipants={handleShowParticipants}
                        handleDownloadReport={handleDownloadReport}
                        getEventTypeLabel={getEventTypeLabel}
                        getEventTypeBadgeClass={getEventTypeBadgeClass}
                      />
                      {group.subEvents.map((sub) => (
                        <EventRow
                          key={sub.id}
                          event={sub}
                          handleEditEvent={handleEditEvent}
                          handleAddUser={handleAddUser}
                          deleteEvent={deleteEvent}
                          handleShowParticipants={handleShowParticipants}
                          handleDownloadReport={handleDownloadReport}
                          getEventTypeLabel={getEventTypeLabel}
                          getEventTypeBadgeClass={getEventTypeBadgeClass}
                        />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          );
        })
        )}
      </div>
    </>
  );
}
